// Ringing calls (HRTH-163). Someone starting a call can ring the conversation: this
// device then shows an incoming-call screen (IncomingCall.vue) with a ringtone until it's
// answered or declined here or on another of this person's devices, the call empties, or
// RING_SECS pass. The caller hears when someone declines.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { RING_SECS, type RingEnd, type RingMode, type UserId, type Uuid } from '../api/types'
import { describeError } from '../api/http'
import { displayName, dmTitle } from '../lib/format'
import { useCall } from './call'
import { useSession } from './session'

export interface Incoming {
  conv: Uuid
  from: UserId
}

/** What starting a call does by default: ring in DMs and groups, notify in channels. */
export function defaultRing(kind: 'channel' | 'dm'): RingMode {
  return kind === 'channel' ? 'notify' : 'ring'
}

const RING_KEY = (conv: Uuid) => `hrth:ring:${conv}`

/** The last choice for this conversation on this device, else the default. */
export function ringChoice(conv: Uuid, kind: 'channel' | 'dm'): RingMode {
  try {
    const v = localStorage.getItem(RING_KEY(conv))
    if (v === 'ring' || v === 'notify') return v
  } catch {
    // storage unavailable
  }
  return defaultRing(kind)
}

export function rememberRing(conv: Uuid, mode: RingMode): void {
  try {
    localStorage.setItem(RING_KEY(conv), mode)
  } catch {
    // storage unavailable
  }
}

export const useRing = defineStore('ring', () => {
  const incoming = ref<Incoming | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null

  /** Who's calling and where, as the incoming-call screen names them. */
  const caller = computed(() => {
    const r = incoming.value
    if (!r) return null
    const s = useSession()
    const name = displayName(s.profiles, r.from)
    for (const sp of s.spaces) {
      const c = sp.channels.find((ch) => ch.id === r.conv)
      if (c) return { name, where: `${sp.name} › #${c.name ?? ''}`, url: `/s/${sp.id}/${c.id}` }
    }
    const dm = s.dms.find((d) => d.id === r.conv)
    const where = dm?.group ? dmTitle(s.profiles, dm.members, s.me, dm.name) : null
    return { name, where, url: `/dm/${r.conv}` }
  })

  /** `tone: false` where the phone is already ringing (opened from its ring notification). */
  function onRing(conv: Uuid, from: UserId, { tone = true } = {}): void {
    const call = useCall()
    if (call.conv === conv && call.state !== 'idle') return
    incoming.value = { conv, from }
    if (tone) startTone()
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => stop(conv), RING_SECS * 1000)
  }

  function onRingEnded(conv: Uuid, _reason: RingEnd): void {
    stop(conv)
  }

  function onDeclined(conv: Uuid, by: UserId): void {
    const s = useSession()
    if (useCall().conv !== conv) return
    s.toast(`${displayName(s.profiles, by)} declined the call`)
  }

  function stop(conv?: Uuid): void {
    if (conv && incoming.value?.conv !== conv) return
    incoming.value = null
    stopTone()
    if (timer) clearTimeout(timer)
    timer = null
  }

  /** Answers: joins the call (from the tap, so phones grant the microphone). */
  async function accept(): Promise<void> {
    const r = incoming.value
    if (!r) return
    stop()
    try {
      await useCall().join(r.conv)
    } catch (e) {
      useSession().toast(`Couldn't join the call: ${describeError(e)}`, 'error')
    }
  }

  async function decline(): Promise<void> {
    const r = incoming.value
    if (!r) return
    stop()
    await useSession()
      .api.declineCall(r.conv)
      .catch(() => undefined)
  }

  // --- Ringtone: two short tones every 2 s, made with Web Audio (no sound file).
  let audio: AudioContext | null = null
  let toneTimer: ReturnType<typeof setInterval> | null = null

  function startTone(): void {
    stopTone()
    try {
      audio = new AudioContext()
    } catch {
      return
    }
    const ctx = audio
    const burst = () => {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
      for (const [at, hz] of [
        [0, 880],
        [0.25, 660],
      ] as const) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.frequency.value = hz
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + at)
        gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + at + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.22)
        osc.connect(gain).connect(ctx.destination)
        osc.start(ctx.currentTime + at)
        osc.stop(ctx.currentTime + at + 0.24)
      }
    }
    burst()
    toneTimer = setInterval(burst, 2000)
  }

  function stopTone(): void {
    if (toneTimer) clearInterval(toneTimer)
    toneTimer = null
    void audio?.close().catch(() => undefined)
    audio = null
  }

  return { incoming, caller, onRing, onRingEnded, onDeclined, stop, accept, decline }
})
